import { matchPath } from 'react-router-dom';

import { readSelectedFishGroupId } from '@/contexts/cageFilter/params';

import type { AnalyticsContextSnapshot } from '../core/types';

import { type PageContext, resolvePageContext } from './pageRegistry';

function parseIntParam(value: string | undefined): number | undefined {
    return value !== undefined ? Number.parseInt(value, 10) : undefined;
}

function buildRouteContext(
    pathname: string,
    clientId: number | undefined,
    locationId: number | undefined,
    cageId: number | undefined,
    pageContext: PageContext,
    fishGroupId: number | undefined
): AnalyticsContextSnapshot {
    return {
        client_id: clientId,
        location_id: locationId,
        cage_id: cageId,
        ...(fishGroupId !== undefined ? { fish_group_id: fishGroupId } : {}),
        ...(pageContext.module_id ? { module_id: pageContext.module_id } : {}),
        main_section: pageContext.main_section || undefined,
        sub_page: pageContext.sub_page || undefined,
        section_id: pageContext.section_id || undefined,
        navigation_level: pageContext.navigation_level,
        required_feature_flags:
            pageContext.feature_flags.length > 0 ? pageContext.feature_flags : undefined,
        page: pathname,
    };
}

/**
 * Builds route context from a pathname without React hooks.
 * Used as a fallback when AnalyticsProvider has not set context yet.
 */
export function getRouteContextFromPathname(
    pathname: string,
    search = ''
): AnalyticsContextSnapshot {
    const clientMatch = matchPath({ path: '/c/:clientId', end: false }, pathname);
    const locationMatch = matchPath(
        { path: '/c/:clientId/location/:locationId', end: false },
        pathname
    );
    const cageMatch = matchPath(
        { path: '/c/:clientId/location/:locationId/pen/:cageId', end: false },
        pathname
    );

    const pageContext = resolvePageContext(pathname);

    return buildRouteContext(
        pathname,
        parseIntParam(clientMatch?.params.clientId),
        parseIntParam(locationMatch?.params.locationId),
        parseIntParam(cageMatch?.params.cageId),
        pageContext,
        readSelectedFishGroupId(search)
    );
}

export { buildRouteContext };
